import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, CircleDollarSign, Clock, XCircle, RotateCcw, EyeOff, Undo2, Send, AlertTriangle, Archive, ArchiveRestore } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const sym = (c) => (c === 'inr' ? '₹' : '$');
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso || '—'; } };

const STATUS_STYLE = {
  paid: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  refunded: 'bg-mahogany/15 text-mahogany',
  partially_refunded: 'bg-amber-50 text-amber-800',
  free: 'bg-mahogany/10 text-mahogany',
};
const TYPE_STYLE = {
  Program: 'bg-rose-soft text-mahogany',
  Membership: 'bg-burgundy/10 text-burgundy',
  Store: 'bg-amber-50 text-amber-800',
  Donation: 'bg-green-50 text-green-800',
};

const AdminPayments = () => {
  const { toast } = useToast();
  const [data, setData] = useState({ payments: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [needsAttention, setNeedsAttention] = useState(false);
  const [q, setQ] = useState('');
  const [recovering, setRecovering] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundMode, setRefundMode] = useState('stripe');
  const [refundPartial, setRefundPartial] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [archivedView, setArchivedView] = useState(false);
  const [selected, setSelected] = useState({});
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkText, setBulkText] = useState('');

  const keyOf = (p) => `${p.id}|${p.type}`;
  const selItems = Object.keys(selected).filter((k) => selected[k]).map((k) => {
    const [id, type] = k.split('|'); return { id, type };
  });

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      if (archivedView) params.archived_only = true;
      const res = await api.get('/admin/payments', { params });
      setData(res.data || { payments: [], summary: {} });
      setSelected({});
    } catch (e) { console.error('Failed to load payments:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPayments(); /* eslint-disable-next-line */ }, [status, archivedView]);

  const openRefund = (p) => {
    setRefundTarget(p); setRefundMode('stripe'); setRefundPartial(false); setRefundAmount('');
  };

  const submitRefund = async () => {
    if (!refundTarget) return;
    setBusy(true);
    try {
      const body = { id: refundTarget.id, type: refundTarget.type, mode: refundMode };
      if (refundPartial && refundAmount) body.amount = Number(refundAmount);
      await api.post('/admin/payments/refund', body);
      toast({ title: refundMode === 'stripe' ? 'Refund processed' : 'Refund flagged', description: refundTarget.customer_name });
      setRefundTarget(null);
      fetchPayments();
    } catch (e) { toast({ title: 'Refund failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const dismiss = async (p) => {
    if (!window.confirm('Dismiss this payment from the list? You can bring it back with the "Show dismissed" filter later.')) return;
    try {
      await api.post('/admin/payments/dismiss', { id: p.id, type: p.type });
      toast({ title: 'Payment dismissed' });
      fetchPayments();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  const recover = async (p) => {
    setRecovering(p.id);
    try {
      const res = await api.post('/admin/crm/payments/recover', { id: p.id, type: p.type });
      toast({ title: 'Recovery email sent', description: res.data?.email });
    } catch (e) { toast({ title: 'Could not send', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setRecovering(null); }
  };

  const retryLink = async (p) => {
    setRecovering(p.id);
    try {
      const res = await api.post('/admin/crm/payments/retry-link', { id: p.id, type: p.type });
      await navigator.clipboard.writeText(res.data.url).catch(() => {});
      toast({ title: 'Payment link ready', description: 'Copied to clipboard — send it to the customer.' });
      fetchPayments();
    } catch (e) { toast({ title: 'Could not create link', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setRecovering(null); }
  };

  const archiveOne = async (p) => {
    if (!window.confirm('Archive this record? It will be hidden from the list but kept for your records (you can restore it from the Archived view).')) return;
    try {
      await api.post('/admin/payments/archive', { items: [{ id: p.id, type: p.type }] });
      toast({ title: 'Archived' });
      fetchPayments();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
  };

  const restoreItems = async (items) => {
    try {
      await api.post('/admin/payments/unarchive', { items });
      toast({ title: `Restored ${items.length} record${items.length > 1 ? 's' : ''}` });
      fetchPayments();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
  };

  const doBulkArchive = async () => {
    setBusy(true);
    try {
      await api.post('/admin/payments/archive', { items: selItems });
      toast({ title: `Archived ${selItems.length} record${selItems.length > 1 ? 's' : ''}` });
      setBulkConfirm(false); setBulkText('');
      fetchPayments();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const toggleSel = (p) => setSelected((s) => ({ ...s, [keyOf(p)]: !s[keyOf(p)] }));

  const attentionRows = needsAttention
    ? (data.payments || []).filter((p) => p.status === 'pending' || p.status === 'failed')
    : (data.payments || []);

  const s = data.summary || {};
  const cur = sym(s.currency);

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <div>
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="font-serif-display text-4xl text-burgundy">Payments</h1>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={CircleDollarSign} label="Total Collected" value={`${cur}${(s.total_collected || 0).toLocaleString()}`}
          sub={`${s.paid_count || 0} paid · ${s.refunded_count || 0} refunded`} tone="green" testid="summary-collected" />
        <SummaryCard icon={Clock} label="Pending" value={s.pending_count || 0} sub="awaiting payment" tone="amber" testid="summary-pending" />
        <SummaryCard icon={XCircle} label="Failed" value={s.failed_count || 0} sub="abandoned / expired" tone="red" testid="summary-failed" />
        <SummaryCard icon={RotateCcw} label="Refund Requested" value={s.refund_requested_count || 0} sub="pending manual refunds" tone="amber" testid="summary-refund-requested" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); fetchPayments(); }} className="flex items-center bg-white/70 rounded-full border border-rose/50 px-4 py-2 gap-2">
          <Search className="w-4 h-4 text-mahogany/70" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, email, item…"
            data-testid="payments-search" className="bg-transparent focus:outline-none text-burgundy w-72" />
        </form>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="payments-status-filter"
          className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none">
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
          <option value="free">Free</option>
        </select>
        <button onClick={() => setNeedsAttention((v) => !v)} data-testid="payments-needs-attention"
          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${needsAttention ? 'bg-amber-100 border-amber-300 text-amber-900' : 'bg-white/70 border-rose/50 text-mahogany hover:bg-rose-soft/40'}`}>
          <AlertTriangle className="w-4 h-4" /> Needs attention
        </button>
        <button onClick={() => setArchivedView((v) => !v)} data-testid="payments-archived-toggle"
          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${archivedView ? 'bg-mahogany text-cream border-mahogany' : 'bg-white/70 border-rose/50 text-mahogany hover:bg-rose-soft/40'}`}>
          <Archive className="w-4 h-4" /> {archivedView ? 'Viewing archived' : 'Archived'}
        </button>
      </div>

      {/* Bulk action bar */}
      {selItems.length > 0 && (
        <div className="flex items-center gap-3 bg-rose-soft/40 border border-rose/50 rounded-2xl px-4 py-3" data-testid="payments-bulk-bar">
          <span className="text-sm text-burgundy font-medium">{selItems.length} selected</span>
          {archivedView ? (
            <button onClick={() => restoreItems(selItems)} data-testid="payments-bulk-restore"
              className="inline-flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors">
              <ArchiveRestore className="w-4 h-4" /> Restore selected
            </button>
          ) : (
            <button onClick={() => { setBulkConfirm(true); setBulkText(''); }} data-testid="payments-bulk-archive"
              className="inline-flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors">
              <Archive className="w-4 h-4" /> Archive selected
            </button>
          )}
          <button onClick={() => setSelected({})} className="text-sm text-mahogany/70 hover:text-mahogany">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white/70 rounded-2xl overflow-hidden border border-rose/50">
            <thead className="bg-rose-soft/60">
              <tr className="text-left text-xs uppercase tracking-widest text-mahogany">
                <th className="px-4 py-3">
                  <input type="checkbox" data-testid="payments-select-all"
                    checked={attentionRows.length > 0 && attentionRows.every((p) => selected[keyOf(p)])}
                    onChange={(e) => {
                      const next = {};
                      if (e.target.checked) attentionRows.forEach((p) => { next[keyOf(p)] = true; });
                      setSelected(next);
                    }} />
                </th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="payments-table-body">
              {attentionRows.map((p, i) => (
                <tr key={p.id || i} className="border-t border-rose/40 hover:bg-rose-soft/20">
                  <td className="px-4 py-3">
                    <input type="checkbox" data-testid={`payments-select-${p.id}`}
                      checked={!!selected[keyOf(p)]} onChange={() => toggleSel(p)} />
                  </td>
                  <td className="px-4 py-3 text-sm text-mahogany/80 whitespace-nowrap">{fmt(p.date)}</td>
                  <td className="px-4 py-3">
                    {p.customer_email && p.customer_email !== '—' ? (
                      <Link to={`/dashboard/admin/customers?open=${encodeURIComponent(p.user_id || p.customer_email)}`}
                        data-testid={`payment-customer-${keyOf(p)}`}
                        className="text-burgundy font-medium hover:text-mahogany hover:underline underline-offset-2 transition-colors">{p.customer_name}</Link>
                    ) : <div className="text-burgundy font-medium">{p.customer_name}</div>}
                    <div className="text-xs text-mahogany/70">{p.customer_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${TYPE_STYLE[p.type] || 'bg-cream-2 text-mahogany'}`}>{p.type}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-burgundy/90">{p.description}</td>
                  <td className="px-4 py-3 font-medium text-burgundy whitespace-nowrap">
                    {sym(p.currency)}{Number(p.amount).toFixed(2)}
                    {p.refunded_amount > 0 && <div className="text-[10px] text-mahogany/60">− {sym(p.currency)}{Number(p.refunded_amount).toFixed(2)} refunded</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-3 py-1.5 rounded-full capitalize ${STATUS_STYLE[p.status] || 'bg-cream-2 text-mahogany'}`}>{String(p.status).replace('_', ' ')}</span>
                    {p.refund_status === 'requested' && <div className="text-[10px] text-amber-700 mt-1">Refund requested</div>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {archivedView ? (
                      <button onClick={() => restoreItems([{ id: p.id, type: p.type }])} data-testid={`restore-btn-${p.id}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-cream bg-burgundy hover:bg-mahogany transition-colors">
                        <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                      </button>
                    ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                    {p.status === 'paid' ? (
                      <button onClick={() => openRefund(p)} data-testid={`refund-btn-${p.id}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-burgundy bg-rose-soft hover:bg-rose-soft/70 transition-colors">
                        <RotateCcw className="w-3.5 h-3.5" /> Refund
                      </button>
                    ) : (p.status === 'refunded' || p.status === 'partially_refunded') ? (
                      <span className="inline-flex items-center gap-1 text-xs text-mahogany/60"><Undo2 className="w-3.5 h-3.5" /> Refunded</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        {(p.status === 'pending' || p.status === 'failed') && p.customer_email && p.customer_email !== '—' && (
                          <>
                            <button onClick={() => recover(p)} disabled={recovering === p.id} data-testid={`recover-btn-${p.id}`}
                              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-burgundy bg-amber-100 hover:bg-amber-200 transition-colors disabled:opacity-60">
                              {recovering === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Recover
                            </button>
                            <button onClick={() => retryLink(p)} disabled={recovering === p.id} data-testid={`retry-link-btn-${p.id}`}
                              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-cream bg-burgundy hover:bg-mahogany transition-colors disabled:opacity-60">
                              <RotateCcw className="w-3.5 h-3.5" /> Retry link
                            </button>
                          </>
                        )}
                        <button onClick={() => dismiss(p)} data-testid={`dismiss-btn-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-mahogany/70 bg-cream-2 hover:bg-rose-soft transition-colors">
                          <EyeOff className="w-3.5 h-3.5" /> Dismiss
                        </button>
                      </div>
                    )}
                    <button onClick={() => archiveOne(p)} data-testid={`archive-btn-${p.id}`}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-red-700 bg-red-50 hover:bg-red-100 transition-colors">
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </button>
                    </div>
                    )}
                  </td>
                </tr>
              ))}
              {attentionRows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-mahogany/60">No payments found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Bulk archive typed-confirm modal */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="bulk-archive-modal">
          <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-serif-display text-2xl text-burgundy">Archive {selItems.length} record{selItems.length > 1 ? 's' : ''}?</h3>
            <p className="text-sm text-mahogany/80">
              These will be hidden from the payments list but kept in your records — you can restore them anytime from the <span className="font-medium text-burgundy">Archived</span> view. To confirm, type <span className="font-semibold text-burgundy">ARCHIVE</span> below.
            </p>
            <input value={bulkText} onChange={(e) => setBulkText(e.target.value)} data-testid="bulk-archive-input"
              placeholder="Type ARCHIVE" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
            <div className="flex justify-end gap-3 pt-1">
              <button onClick={() => { setBulkConfirm(false); setBulkText(''); }} data-testid="bulk-archive-cancel"
                className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
              <button onClick={doBulkArchive} disabled={busy || bulkText.trim().toUpperCase() !== 'ARCHIVE'} data-testid="bulk-archive-confirm"
                className="text-sm px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund modal */}
      {refundTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="refund-modal">
          <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-serif-display text-2xl text-burgundy">Refund payment</h3>
            <p className="text-sm text-mahogany/80">
              {refundTarget.customer_name} · {refundTarget.description} · <span className="font-medium text-burgundy">{sym(refundTarget.currency)}{Number(refundTarget.amount).toFixed(2)}</span>
            </p>

            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-mahogany/70">How would you like to refund?</div>
              <label className="flex items-start gap-2 p-3 rounded-2xl border border-rose/40 bg-white/60 cursor-pointer" data-testid="refund-mode-stripe">
                <input type="radio" name="refundmode" checked={refundMode === 'stripe'} onChange={() => setRefundMode('stripe')} className="mt-1" />
                <div><div className="text-sm font-medium text-burgundy">Process via Stripe</div><div className="text-xs text-mahogany/60">Money is returned to the customer automatically.</div></div>
              </label>
              <label className="flex items-start gap-2 p-3 rounded-2xl border border-rose/40 bg-white/60 cursor-pointer" data-testid="refund-mode-manual">
                <input type="radio" name="refundmode" checked={refundMode === 'manual'} onChange={() => setRefundMode('manual')} className="mt-1" />
                <div><div className="text-sm font-medium text-burgundy">Flag for manual refund</div><div className="text-xs text-mahogany/60">Mark as "refund requested" for finance to handle.</div></div>
              </label>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-burgundy">
                <input type="checkbox" checked={refundPartial} onChange={(e) => setRefundPartial(e.target.checked)} data-testid="refund-partial-toggle" />
                Partial refund
              </label>
              {refundPartial && (
                <input type="number" min="0" step="0.01" max={refundTarget.amount} value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)} placeholder={`Amount (max ${Number(refundTarget.amount).toFixed(2)})`}
                  data-testid="refund-amount-input"
                  className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
              )}
            </div>

            <div className="flex justify-end gap-3 pt-1">
              <button onClick={() => setRefundTarget(null)} data-testid="refund-cancel"
                className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
              <button onClick={submitRefund} disabled={busy || (refundPartial && !refundAmount)} data-testid="refund-confirm"
                className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {refundMode === 'stripe' ? 'Refund now' : 'Flag refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const toneMap = {
  green: 'text-green-700', amber: 'text-amber-700', red: 'text-red-700',
};
const SummaryCard = ({ icon: Icon, label, value, sub, tone, testid }) => (
  <div className="p-5 rounded-2xl border border-rose/50 bg-white/70" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-mahogany/70">
      <Icon className={`w-4 h-4 ${toneMap[tone]}`} /> {label}
    </div>
    <div className="font-serif-display text-3xl text-burgundy mt-2">{value}</div>
    <div className="text-xs text-mahogany/60 mt-1">{sub}</div>
  </div>
);

export default AdminPayments;
